import { useEffect, useState } from 'react';
import { AnimatePresence, LazyMotion, MotionConfig } from 'motion/react';
import { IconContext } from '@phosphor-icons/react';
import { setupPwa } from './pwa/register';
import { useApp } from './state/store';
import { Banners, Announcer, TopBar, Toasts } from './ui/Chrome';
import { Hero } from './ui/Hero';
import { Panels } from './ui/Panels';
import { Workspace } from './ui/Workspace';
import { useShortcuts } from './ui/useShortcuts';

export function App() {
  const boot = useApp((s) => s.boot);
  const booted = useApp((s) => s.booted);
  const activeId = useApp((s) => s.activeId);
  const focus = useApp((s) => s.focus);
  const motionPref = useApp((s) => s.settings.motion);
  const toast = useApp((s) => s.toast);
  const [updater, setUpdater] = useState<(() => Promise<void>) | null>(null);
  useShortcuts();

  useEffect(() => {
    void boot();
    setupPwa({
      onNeedRefresh: (apply) => setUpdater(() => apply),
      onOfflineReady: () =>
        toast({
          kind: 'success',
          title: 'OneFlow now opens offline',
          body: 'Downloads still need a connection.',
        }),
    });
    const on = () => useApp.setState({ online: true });
    const off = () => useApp.setState({ online: false });
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, [boot, toast]);

  return (
    <LazyMotion features={() => import('./motion-features').then((f) => f.default)} strict>
      <MotionConfig
        reducedMotion={
          motionPref === 'reduce' ? 'always' : motionPref === 'full' ? 'never' : 'user'
        }
      >
        <IconContext.Provider value={{ weight: 'regular', size: '1.15em' }}>
          <div className="app" data-focus={focus || undefined} data-motion={motionPref}>
            <a className="skip" href="#main">
              Skip to content
            </a>
            <TopBar />
            <Banners updater={updater} />
            <main id="main" className="main">
              {!booted ? (
                <p className="boot" role="status">
                  Opening your files…
                </p>
              ) : (
                <AnimatePresence mode="wait" initial={false}>
                  {activeId ? (
                    <Workspace key={`w-${activeId}`} id={activeId} />
                  ) : (
                    <Hero key="hero" />
                  )}
                </AnimatePresence>
              )}
            </main>
            <Panels />
            <Toasts />
            <Announcer />
          </div>
        </IconContext.Provider>
      </MotionConfig>
    </LazyMotion>
  );
}
